import { useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFaceSmilePlus } from '@fortawesome/pro-regular-svg-icons';
import { Chip } from '@design-system/components/chip/chip';
import { reactionEmoji } from '../lib/reactionEmoji';
import { useReactionsBar } from '../lib/useReactionsBar';
import { Reaction, ReactionType } from '../model/post.types';
import { AddReactionPopover } from './AddReactionPopover';

type Props = {
  reactions: Reaction[];
  onToggle: (type: ReactionType) => Promise<unknown> | void;
};

export const PostReactions = ({ reactions, onToggle }: Props) => {
  const { visibleReactions, restCount, expanded, toggleExpanded } =
    useReactionsBar(reactions);
  const [pending, setPending] = useState<ReadonlySet<ReactionType>>(new Set());
  const addRef = useRef<HTMLDivElement>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const handleToggle = (type: ReactionType) => {
    if (pending.has(type)) return;

    setPending((current) => new Set(current).add(type));
    Promise.resolve(onToggle(type)).finally(() =>
      setPending((current) => {
        const next = new Set(current);
        next.delete(type);
        return next;
      }),
    );
  };

  return (
    <div className="flex flex-row flex-wrap items-center gap-1.5">
      {visibleReactions.map((reaction) => (
        <Chip
          key={reaction.type}
          selected={reaction.isToggled}
          disabled={pending.has(reaction.type)}
          label={`${reactionEmoji(reaction.type)} ${reaction.count}`}
          onClick={() => handleToggle(reaction.type)}
        />
      ))}
      {restCount > 0 && (
        <Chip
          label={expanded ? 'Show less' : `${restCount} more`}
          onClick={toggleExpanded}
        />
      )}
      <Chip
        ref={addRef}
        label={<FontAwesomeIcon icon={faFaceSmilePlus} />}
        onClick={() => setPickerOpen(true)}
      />
      <AddReactionPopover
        open={pickerOpen}
        anchorEl={addRef.current}
        onClose={() => setPickerOpen(false)}
        onSelect={(type) => {
          setPickerOpen(false);
          handleToggle(type);
        }}
      />
    </div>
  );
};
